import { useState } from 'react';

import { useAuthStore } from '../store/authStore.js';

export function LoginPage() {
  const login = useAuthStore((s) => s.login);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await login(username.trim(), password);
    } catch (err) {
      // The server returns an identical body for a wrong password and an unknown
      // username, so there is nothing here to distinguish them - by design.
      setError(
        err.status === 423
          ? `Account locked after repeated failed sign-ins. Try again later${
              err.details?.locked_until ? '.' : '.'
            }`
          : err.status === 429
            ? 'Too many attempts from this network. Wait a few minutes.'
            : err.status === 403
              ? 'This account has been deactivated.'
              : 'Incorrect username or password.',
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="centre-page">
      <div className="centre-card">
        <div className="panel">
          <div className="panel-body">
            <div className="brand">
              <h1>Caption Annotation</h1>
              <p>Hallucination evaluation study</p>
            </div>

            <form className="stack" onSubmit={submit}>
              <div className="field">
                <label htmlFor="username">Username</label>
                <input
                  id="username"
                  className="input"
                  value={username}
                  autoComplete="username"
                  autoFocus
                  required
                  onChange={(e) => setUsername(e.target.value)}
                />
              </div>

              <div className="field">
                <label htmlFor="password">Password</label>
                <input
                  id="password"
                  className="input"
                  type="password"
                  value={password}
                  autoComplete="current-password"
                  required
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>

              {error && (
                <div className="banner danger" role="alert">
                  {error}
                </div>
              )}

              <button type="submit" className="btn primary lg block" disabled={busy}>
                {busy ? 'Signing in…' : 'Sign in'}
              </button>
            </form>

            <p className="tiny faint" style={{ marginTop: 'var(--space-4)', textAlign: 'center' }}>
              Accounts are created by the study administrator. There is no self sign-up.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * The password form itself, shared by the forced first-sign-in page and the voluntary
 * one in the sidebar.
 *
 * One implementation on purpose: when the forced page was the only way to change a
 * password, waiving the requirement left an account with no way to change it at all.
 * Two copies of this form would have drifted the moment either grew a rule.
 */
export function ChangePasswordForm({ onDone, submitLabel = 'Change password' }) {
  const changePassword = useAuthStore((s) => s.changePassword);
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async (event) => {
    event.preventDefault();
    if (next !== confirm) {
      setError('The two new passwords do not match.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await changePassword(current, next);
      onDone?.();
    } catch (err) {
      setError(
        err.details?.rules
          ? `Password ${err.details.rules.join(', ')}.`
          : err.message || 'Could not change the password.',
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="stack" onSubmit={submit}>
      <div className="field">
        <label htmlFor="current">Current password</label>
        <input
          id="current"
          className="input"
          type="password"
          autoComplete="current-password"
          value={current}
          required
          onChange={(e) => setCurrent(e.target.value)}
        />
      </div>
      <div className="field">
        <label htmlFor="next">New password</label>
        <input
          id="next"
          className="input"
          type="password"
          autoComplete="new-password"
          value={next}
          required
          onChange={(e) => setNext(e.target.value)}
        />
        <span className="hint">At least 12 characters.</span>
      </div>
      <div className="field">
        <label htmlFor="confirm">Repeat new password</label>
        <input
          id="confirm"
          className="input"
          type="password"
          autoComplete="new-password"
          value={confirm}
          required
          onChange={(e) => setConfirm(e.target.value)}
        />
      </div>

      {error && (
        <div className="banner danger" role="alert">
          {error}
        </div>
      )}

      <button type="submit" className="btn primary lg block" disabled={busy}>
        {busy ? 'Saving…' : submitLabel}
      </button>
    </form>
  );
}

export function ChangePasswordPage() {
  const user = useAuthStore((s) => s.user);

  return (
    <div className="centre-page">
      <div className="centre-card">
        <div className="panel">
          <div className="panel-body">
            <div className="brand">
              <h1>Choose a password</h1>
              <p>
                Signed in as {user?.display_name}. Set your own password before continuing.
              </p>
            </div>

            <ChangePasswordForm submitLabel="Set password and continue" />
          </div>
        </div>
      </div>
    </div>
  );
}
